import { isEqual } from 'lodash'

import { AttendeeRole, EventType } from '@/constants'
import { ScriptAttendee, ZoomParticipant } from '@/types'
import { getAttendeeRolesForEvent } from '@/utils/parrot'

export const shouldPreserveZoomParticipants = (
  changedFieldName?: string,
): boolean => !changedFieldName?.includes('zoom_participants')

export type WitnessRoleMode = 'selectable' | 'hidden' | 'locked'

export const getSelectableAttendeeRoles = ({
  witnessRole,
  currentRole,
  eventType,
}: {
  witnessRole: WitnessRoleMode
  currentRole?: AttendeeRole
  eventType: EventType
}): AttendeeRole[] =>
  getAttendeeRolesForEvent({ type: eventType }).filter(
    attendeeRole =>
      witnessRole === 'selectable' ||
      attendeeRole !== AttendeeRole.WITNESS ||
      currentRole === AttendeeRole.WITNESS,
  )

export const isWitnessRoleLocked = ({
  witnessRole,
  currentRole,
}: {
  witnessRole: WitnessRoleMode
  currentRole?: AttendeeRole
}): boolean => witnessRole === 'locked' && currentRole === AttendeeRole.WITNESS

export type OpeningAttendeesAutosave = {
  attendees: ScriptAttendee[]
  preserveExistingZoomParticipants: boolean
}

export const getIdentityDocumentExemptAttendeeIds = (
  attendees: ScriptAttendee[],
): string[] => {
  const hasGuardian = attendees.some(
    attendee =>
      attendee.role === AttendeeRole.GUARDIAN && attendee.present === true,
  )

  if (!hasGuardian) {
    return []
  }

  return attendees
    .filter(attendee => attendee.role === AttendeeRole.WITNESS)
    .map(attendee => attendee.attendee_id)
}

const isAttendeeFieldChange = (changedFieldName?: string): boolean =>
  !!changedFieldName?.startsWith('attendees.')

const isAttendeesChange = (changedFieldName?: string): boolean =>
  !!changedFieldName?.startsWith('attendees')

// Only the edited field is validated, while the whole array is persisted, so an
// invalid value abandoned in another row can still ride along on a later valid
// save. Accepted: validating more than the edited field blocks witness and
// guardian edits for as long as their identity document is incomplete.
export const getOpeningAttendeeValidationPaths = (
  changedFieldName?: string,
): string[] | undefined =>
  isAttendeeFieldChange(changedFieldName)
    ? [changedFieldName!, 'attendees.global.witness']
    : undefined

export const getOpeningAttendeesAutosave = ({
  changedFieldName,
  attendees,
  isValid,
  hasEditedZoomParticipants,
  storeAttendees,
}: {
  changedFieldName: string | undefined
  attendees: ScriptAttendee[]
  isValid: boolean
  hasEditedZoomParticipants: boolean
  storeAttendees?: ScriptAttendee[]
}): OpeningAttendeesAutosave | undefined => {
  // The opening step re-syncs the form from the store on every store change, and
  // that setValue echoes back through the same watch subscription that triggers
  // this autosave. Saving an array the store already holds would produce a new
  // store object, re-run the sync, and loop, so drop those echoes here.
  if (storeAttendees !== undefined && isEqual(attendees, storeAttendees)) {
    return undefined
  }

  if (!isAttendeesChange(changedFieldName)) {
    return undefined
  }

  if (isAttendeeFieldChange(changedFieldName) && !isValid) {
    return undefined
  }

  return {
    attendees,
    preserveExistingZoomParticipants: !hasEditedZoomParticipants,
  }
}

// Each attendee's own errors render on its card, so a step-level FormErrors that
// flattens the whole tree would say the same thing twice. It still has to carry
// what no card can: the array-level rules report at attendees.global and
// attendees.root, and every field beside attendees reports nowhere else.
export const omitPerAttendeeErrors = <T extends Record<string, unknown>>(
  errors: T,
): T => {
  const attendees = errors.attendees as Record<string, unknown> | undefined

  if (!attendees) {
    return errors
  }

  return {
    ...errors,
    attendees: Object.fromEntries(
      Object.entries(attendees).filter(([key]) => !/^\d+$/.test(key)),
    ),
  }
}

// identity_document is validated and reported on its own, by IdentityDocumentInput
// and IdentityDocumentCard, so attendee-level error rendering leaves it out.
export const omitIdentityDocumentErrors = <T extends object>(
  errors: T | undefined,
): T | undefined =>
  errors
    ? (Object.fromEntries(
        Object.entries(errors).filter(([key]) => key !== 'identity_document'),
      ) as T)
    : errors

// A card reports only the fields it renders, and isWitnessLocationEnabled is
// what says whether city and state are among them. Where it is false they are
// answered elsewhere - by the opening step's witness location question, which
// holds both the inputs and their message - and naming a field the card does not
// show points the reporter at a value they can neither see nor correct.
//
// Composed with omitIdentityDocumentErrors rather than folded into it: that one
// drops a field no card ever owns, this one a field some do.
export const omitWitnessLocationErrors = <T extends object>(
  errors: T | undefined,
  isWitnessLocationEnabled: boolean,
): T | undefined =>
  errors && !isWitnessLocationEnabled
    ? (Object.fromEntries(
        Object.entries(errors).filter(
          ([key]) => key !== 'city' && key !== 'state',
        ),
      ) as T)
    : errors

// The attendee editor's Save is gated on these validating, so a field it does
// not render must not be among them: with no input on screen nothing the
// reporter could do would satisfy the rule, and the button would go dead with
// nothing on the card to explain it. identity_document is never rendered here -
// IdentityDocumentInput captures it - and city and state only where
// isWitnessLocationEnabled says so, the opening step asking for the witness
// location in its own question instead.
export const getEditedAttendeeFieldPaths = (
  namePrefix: string,
  isWitnessLocationEnabled: boolean,
): string[] => [
  `${namePrefix}.name`,
  `${namePrefix}.email`,
  `${namePrefix}.role`,
  `${namePrefix}.present`,
  `${namePrefix}.zoom_participants`,
  ...(isWitnessLocationEnabled
    ? [`${namePrefix}.city`, `${namePrefix}.state`]
    : []),
]

// A prefixed attendee sits at `<array>.<index>` on the enclosing form, so the
// set it is being edited alongside is the array its own prefix names. Reading it
// there rather than from a prop keeps the warnings on the live values, and
// deriving the path from the prefix assumes no particular key - an attendee that
// is the whole form has no prefix, gets nothing back, and falls through to what
// its caller passed.
export const getSiblingsPath = (namePrefix: string): string | undefined =>
  namePrefix.match(/^(.+)\.\d+$/)?.[1]

// The cross-attendee warnings count collisions within a set and then ask whether
// this attendee is one of them, so the set has to hold this attendee as it
// currently reads. Callers pass the stored attendees, which omit one still being
// added and hold a stale copy of one being edited; substituting by id lets
// neither hide a collision, and is a no-op where the set is already live.
export const withCurrentAttendee = (
  allAttendees: ScriptAttendee[],
  currentAttendee: ScriptAttendee,
): ScriptAttendee[] =>
  allAttendees.some(
    ({ attendee_id }) => attendee_id === currentAttendee.attendee_id,
  )
    ? allAttendees.map(attendee =>
        attendee.attendee_id === currentAttendee.attendee_id
          ? currentAttendee
          : attendee,
      )
    : [...allAttendees, currentAttendee]

export const hasAttendeeFieldErrors = (errors: object | undefined): boolean =>
  Object.keys(omitIdentityDocumentErrors(errors) ?? {}).length > 0

export const getFieldName = (fieldName: string, prefix?: string) => {
  if (prefix !== undefined) {
    return `${prefix}.${fieldName}`
  }

  return fieldName
}

export const transformToZoomParticipants = (
  values: string[],
  zoomParticipants: ZoomParticipant[],
): ZoomParticipant[] => {
  return values.map(zpId => zoomParticipants.find(p => zpId === p.id)!)
}

export const getCombinedId = (zoomParticipant: ZoomParticipant): string => {
  return `${zoomParticipant.meeting_instance_id}:${zoomParticipant.user_id}`
}
